/** Выбор точки доставки на карте.
 *
 * Открывается сразу на человеке: спрашиваем геопозицию и ставим маркер по
 * центру — отдельной кнопки «я здесь» быть не должно, это стартовое
 * состояние. Маркер двигается тапом и перетаскиванием, кружок-прицел
 * возвращает карту к текущему месту, как в навигаторе.
 *
 * Последняя известная точка помнится между открытиями: второй заход не
 * начинается с чужого города, пока геолокация думает.
 */
import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { tap, tg } from '@/shared/tg';

/** Куда смотреть, если про человека вообще ничего не известно. */
const FALLBACK: [number, number] = [53.9006, 27.559];
const REMEMBERED = 'parbet-last-point';

export interface PickedPoint {
  lat: number;
  lon: number;
  label?: string;
}

function lastKnown(): [number, number] | null {
  try {
    const kept = JSON.parse(localStorage.getItem(REMEMBERED) ?? 'null');
    return Array.isArray(kept) && kept.length === 2 ? (kept as [number, number]) : null;
  } catch {
    return null;
  }
}

/** Координаты человека: сперва телеграмовский менеджер, затем браузер.
 *
 * Ответ приходит всегда: менеджер Telegram умеет молчать вечно, если человек
 * не ответил на запрос доступа, — тогда через таймаут пробуем браузер.
 */
function whereAmI(): Promise<[number, number] | null> {
  return new Promise((resolve) => {
    let answered = false;
    const done = (at: [number, number] | null) => {
      if (answered) return;
      answered = true;
      if (at) {
        try {
          localStorage.setItem(REMEMBERED, JSON.stringify(at));
        } catch {
          /* приватный режим — переживём */
        }
      }
      resolve(at);
    };
    setTimeout(() => done(null), 9000);

    const browser = () => {
      if (!navigator.geolocation) return done(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => done([pos.coords.latitude, pos.coords.longitude]),
        () => done(null),
        { timeout: 7000, enableHighAccuracy: true },
      );
    };

    const app = tg() as unknown as {
      LocationManager?: {
        init(cb?: () => void): void;
        getLocation(cb: (data: { latitude: number; longitude: number } | null) => void): void;
        isInited?: boolean;
      };
    } | null;
    const manager = app?.LocationManager;
    if (!manager) return browser();
    try {
      const ask = () =>
        manager.getLocation((data) =>
          data ? done([data.latitude, data.longitude]) : browser());
      if (manager.isInited) ask();
      else manager.init(ask);
    } catch {
      browser();
    }
  });
}

/** Подпись для точки — лучший доступный адрес, молча без него. */
async function labelFor(lat: number, lon: number): Promise<string | undefined> {
  try {
    const answer = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}` +
        `&format=jsonv2&accept-language=ru&zoom=18`,
      // Геокодер молчит — кнопка не должна зависнуть на «Секунду…»:
      // через четыре секунды едем без подписи, точка важнее адреса
      { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(4000) },
    ).then((r) => r.json());
    const a = answer.address ?? {};
    const parts = [
      [a.road, a.house_number].filter(Boolean).join(' '),
      a.city || a.town || a.village,
    ].filter(Boolean);
    return parts.join(', ') || undefined;
  } catch {
    return undefined;
  }
}

const PIN = L.divIcon({
  html: '<div style="font-size:32px;line-height:1;transform:translateY(-15px)">📍</div>',
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

export function MapPick({
  start,
  onDone,
  onClose,
}: {
  /** Уже выбранная точка: открываемся на ней, а не ищем человека заново */
  start?: PickedPoint | null;
  onDone: (point: PickedPoint) => void;
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markRef = useRef<L.Marker | null>(null);
  const [picked, setPicked] = useState<[number, number] | null>(
    start ? [start.lat, start.lon] : null,
  );
  const [hint, setHint] = useState(true);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(!start);

  /** Поставить маркер и запомнить выбор. */
  const place = (at: [number, number], move = true) => {
    const map = mapRef.current;
    if (!map) return;
    setPicked(at);
    if (markRef.current) markRef.current.setLatLng(at);
    else {
      const mark = L.marker(at, { icon: PIN, draggable: true }).addTo(map);
      mark.on('dragend', () => {
        const p = mark.getLatLng();
        tap();
        setPicked([p.lat, p.lng]);
      });
      markRef.current = mark;
    }
    if (move) map.setView(at, Math.max(map.getZoom(), 16));
  };

  useEffect(() => {
    if (!box.current) return;
    const first = start ? ([start.lat, start.lon] as [number, number]) : lastKnown();
    const map = L.map(box.current, { zoomControl: false, attributionControl: false })
      .setView(first ?? FALLBACK, first ? 16 : 11);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    mapRef.current = map;
    if (start) place([start.lat, start.lon], false);

    map.on('click', (event: L.LeafletMouseEvent) => {
      tap();
      setHint(false);
      place([event.latlng.lat, event.latlng.lng], false);
    });

    // Открываемся на человеке: маркер встаёт сам, двигать — по желанию
    void whereAmI().then((me) => {
      setLocating(false);
      if (!me || !mapRef.current) return;
      if (!start && !markRef.current) place(me);
      else mapRef.current.setView(me, 15);
    });

    return () => {
      map.remove();
      mapRef.current = null;
      markRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Подсказка живёт несколько секунд: дальше она только мешает карте
  useEffect(() => {
    if (!hint) return;
    const timer = setTimeout(() => setHint(false), 6000);
    return () => clearTimeout(timer);
  }, [hint]);

  const backToMe = () => {
    tap();
    setLocating(true);
    void whereAmI().then((me) => {
      setLocating(false);
      if (!me || !mapRef.current) return;
      mapRef.current.setView(me, 16);
      if (!markRef.current) place(me, false);
    });
  };

  const finish = async () => {
    if (!picked || busy) return;
    setBusy(true);
    const [lat, lon] = picked;
    const label = await labelFor(lat, lon);
    onDone({ lat, lon, label });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-night">
      <div className="relative min-h-0 flex-1">
        <div ref={box} className="absolute inset-0" />

        {/* Подсказка: маркер уже стоит, его можно двигать */}
        {hint && (
          <div className="animate-fade pointer-events-none absolute inset-x-3 top-3 z-[500] rounded-xl bg-night/85 px-3 py-2 text-center text-[12.5px] text-frost backdrop-blur-[8px]">
            {locating
              ? 'Определяю, где вы…'
              : 'Маркер можно перетащить или тапнуть по нужному месту'}
          </div>
        )}

        {/* Прицел — вернуться к своему месту, как в навигаторе */}
        <button
          type="button"
          aria-label="Моё местоположение"
          onClick={backToMe}
          className="absolute right-3 bottom-3 z-[500] grid size-11 place-items-center rounded-full border border-glass-line bg-night/90 text-[19px] backdrop-blur-[8px] active:bg-glass-active"
        >
          {locating ? '…' : '🎯'}
        </button>
      </div>

      <div className="flex gap-2 border-t border-glass-line bg-night p-3 pb-[max(12px,env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={() => {
            tap();
            onClose();
          }}
          className="rounded-xl border border-glass-line bg-glass px-5 py-3 text-[14px] font-[650] text-frost transition-colors duration-150 active:bg-glass-active"
        >
          Отмена
        </button>
        <button
          type="button"
          disabled={!picked || busy}
          onClick={() => void finish()}
          className="flex-1 rounded-xl bg-linear-135 from-mint to-mint-deep py-3 text-[14px] font-[680] text-mint-ink transition-transform duration-150 active:scale-[.98] disabled:opacity-50"
        >
          {busy ? 'Секунду…' : picked ? 'Готово' : 'Тапните точку на карте'}
        </button>
      </div>
    </div>
  );
}
